import { Box, Skeleton, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY } from '../../../../config/appConfig';
import { InvestorType } from '../../../../constants/investorType';
import { useInvestorDetails } from '../../../../context/investorDetailContext';
import { currencyFormatterHelper } from '../../../../helpers/currencyFormatterHelper';
import { LabelBadge } from '../../../../shared/components/common/labelBadge';
import { SummaryCard } from '../../../../shared/components/common/summaryCard';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { InvestorDashboardBorrowers } from './borrowers';
import { InvestorDashboardLoans } from './loans';
import { InvestorDashboardService } from './investorDashboardService';

const StatCard = ({ label, value, valueClass = '' }) => (
	<SummaryCard classList="!p-4">
		<Box className="flex flex-col gap-1">
			<TextWrapper classList="!text-xs !text-investor-slate" text={label} />
			<Box className={`!text-2xl !font-bold !text-investor-deep ${valueClass}`}>{value}</Box>
		</Box>
	</SummaryCard>
);

export const InvestorDashboard = () => {
	const { t } = useTranslation();
	const { investorDetails } = useInvestorDetails();
	const investorDashboardService = new InvestorDashboardService();
	const [tabIndex, setTabIndex] = useState(0);
	const [summary, setSummary] = useState(null);

	const tabs = [
		{ name: t('borrowers'), key: 'borrowers' },
		{ name: t('loans'), key: 'loans' },
	];

	const hasAccess =
		investorDetails?.investorType?.name === InvestorType.Corporate &&
		investorDetails?.integrationEnabled;

	useEffect(() => {
		if (hasAccess) {
			investorDashboardService
				.getSummary()
				.then((resp) => setSummary(resp.data))
				.catch((error) => console.error('Failed to load investor dashboard summary', error));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAccess]);

	if (investorDetails === null) {
		return <Skeleton variant="rounded" height={320} sx={{ borderRadius: '30px', width: '100%' }} />;
	}

	if (!hasAccess) {
		return (
			<Box className="text-center text-investor-slate p-12">
				{t('investorDashboardNoAccess')}
			</Box>
		);
	}

	return (
		<Box className="flex flex-col gap-6 w-full">
			<Box className="flex items-center gap-3">
				<Box className="!text-2xl !font-bold !text-investor-deep">
					{t('investorDashboardTitle')}
				</Box>
				<LabelBadge
					label={t('investorDashboardCorporateBadge')}
					bgColor="#C6FEE5"
					textColor="#063236"
					classList="!rounded-full !px-3 !py-1 !text-[11px] !font-bold !normal-case"
				/>
			</Box>

			<Box className="grid grid-cols-2 md:grid-cols-4 gap-4">
				<StatCard label={t('totalBorrowers')} value={summary?.totalBorrowers ?? '-'} />
				<StatCard
					label={t('activeLoans')}
					value={summary?.activeLoans ?? '-'}
					valueClass="!text-investor-green"
				/>
				<StatCard
					label={t('totalDeployed')}
					value={
						summary
							? `${CURRENCY} ${currencyFormatterHelper(summary?.totalDeployed, 0)}`
							: '-'
					}
				/>
				<StatCard
					label={t('lateOrDefaulted')}
					value={summary?.lateOrDefaultedLoans ?? '-'}
					valueClass="!text-red-500"
				/>
			</Box>

			<Box sx={{ backgroundColor: '#f3f5f5', borderRadius: '30px', overflow: 'hidden', p: 2 }}>
				<Box className="bg-white rounded-2xl overflow-hidden mb-4">
					<Tabs
						value={tabIndex}
						onChange={(e, newIndex) => setTabIndex(newIndex)}
						indicatorColor="primary"
						textColor="inherit"
						className="!px-2">
						{tabs.map((tab) => (
							<Tab key={tab.key} label={tab.name} className="!normal-case !font-bold" />
						))}
					</Tabs>
				</Box>

				<Box sx={{ overflow: 'hidden' }}>
					{tabIndex === 0 ? <InvestorDashboardBorrowers /> : <InvestorDashboardLoans />}
				</Box>
			</Box>
		</Box>
	);
};
